/* eslint-disable react/no-danger */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'font-awesome/css/font-awesome.min.css';
import styled from 'styled-components';
import './footer.css';
import SocialLinks from './SocialLinks';
import FooterLinks from './FooterLinks';
import FooterNotes from './FooterNotes';
import FooterBottomLinks from './FooterBottomLinks';
import FooterTelephone from './FooterTelephone';
import FooterExpiry from './FooterExpiry';
import { sanitizeInnerHtml } from '../../utils';

// eslint-disable-next-line prefer-destructuring
const version = require('../../../package.json').version;

const FooterRoot = styled.div`
  z-index: 1;
  background-color: ${(props) => props.backgroundColor || 'rgba(59, 83, 105, 1)'};
  color: ${(props) => props.color};
  ${(props) =>
    !props.removeFooterMargin &&
    `padding-top: 20px;
    padding-bottom: 20px;`}
  font-family: ${(props) => props.fontFamily || 'inherit'}
`;

const Img = styled.img`
  height: auto;
  width: auto;
  max-width: 150px;
`;

const Bold = styled.p`
  font-weight: bold;
`;

const GlobalFooter = styled.div`
  ${(props) => !props.removeFooterMargin && 'padding: 50px 0;'}
  background: ${({ background }) => background};
  @media (forced-colors: active) {
    border-top: 1px solid Highlight;
  }
`;

const Partners = styled.div`
  margin: 20px 0;
`;
const DisclaimerText = styled.div`
  padding: 20px;
  background-color: ${(props) => props.backgroundColor};
  color: ${(props) => props.color};
`;

const DynamicContent = styled.div`
  min-height: ${({ isContentLoaded }) => (isContentLoaded ? '23px' : '60px')};
`;

function Footer({ footer, custCareTel, policyExpiry }) {
  const {
    color,
    background,
    link,
    altText,
    logo,
    phoneText,
    phoneNumber,
    footerLinksTop,
    socialLinks,
    disclaimerDesc,
    footerLinksBottom,
    isHTML,
    footerHTML,
    removeFooterMargin,
    showDisclaimerText,
    fontColor,
    disclaimerBackground,
  } = footer;

  const [htmlContent, setHtmlContent] = useState('');
  const [isContentLoaded, setIsContentLoaded] = useState(false);

  useEffect(() => {
    if (footerHTML) {
      setTimeout(() => {
        setHtmlContent(sanitizeInnerHtml(footer.footerHTML, true));
        setIsContentLoaded(true);
      }, 300);
    }
  }, [footerHTML]);

  return (
    <footer>
      <FooterRoot
        color={ color }
        name='FooterRoot'
        backgroundColor={ background }
        removeFooterMargin={ removeFooterMargin }
        fontFamily={ footer.fontFamily }
      >
        <GlobalFooter
          background={ background }
          removeFooterMargin={ removeFooterMargin }
          className='global-footer'
        >
          {isHTML ? (
            <>
              {custCareTel && <FooterTelephone custCareTel={ custCareTel } />}
              {/* nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml */}
              <DynamicContent
                className='container'
                isContentLoaded={ isContentLoaded }
                dangerouslySetInnerHTML={ {
                  __html: htmlContent || '',
                } }
              />
              <FooterNotes />
              {policyExpiry && <FooterExpiry policyExpiry={ policyExpiry } />}
            </>
          ) : (
            <div className='container'>
              <div className='header-information-footer'>
                <div className='company-logo'>
                  {link ? (
                    <a href={ link } target='_blank' rel='noopener noreferrer'>
                      <Img src={ logo } alt={ altText } />
                    </a>
                  ) : (
                    <Img src={ logo } alt={ altText } />
                  )}
                </div>
                <div className='contact-number'>
                  <div>
                    <strong>{`${phoneText} `}</strong>
                    {phoneNumber ? <a href={ `tel:${phoneNumber}` }>{`(${phoneNumber})`}</a> : null}
                  </div>
                </div>
              </div>
              <SocialLinks links={ socialLinks } />
              <div className='footer-text-container'>
                {footerLinksTop && <FooterLinks links={ footerLinksTop } />}
                {footer.businessPartners && (
                  <Partners>
                    <span className='disclaimer-text'>{footer.businessPartners}</span>
                    <FooterLinks links={ footer.footerBusinessLinks } />
                  </Partners>
                )}
                <p className='disclaimer-text'>{disclaimerDesc}</p>
                {footer.footerTextBottom && <Bold>{footer.footerTextBottom}</Bold>}
                {footerLinksBottom && <FooterBottomLinks links={ footerLinksBottom } />}
              </div>
              <div className='version-container'>
                <span>v{version}</span>
              </div>
            </div>
          )}
        </GlobalFooter>
        {showDisclaimerText && (
          <DisclaimerText color={ fontColor } backgroundColor={ disclaimerBackground }>
            {disclaimerDesc}
          </DisclaimerText>
        )}
      </FooterRoot>
    </footer>
  );
}

Footer.propTypes = {
  footer: PropTypes.object.isRequired,
};

export default Footer;
